import { SocialServiceOffersSelectInputStore } from './social-service-offers-select-input.store';
import { fb, translate } from '../../../modules/shared/shared.global-service';
import { Component, computed, effect, inject, input, signal, viewChildren, ChangeDetectionStrategy } from '@angular/core';
import { WorkOfferUpsertInput } from '@libs/types/src/services/input-type/work/work-offer.upsert-input';
import { FormGroup, Validators } from '@angular/forms';
import { changeSignal } from '../../../utils/form.utils';
import { CheckInputComponent } from '../../kit/input/check-input/check-input.component';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { InfiniteScrollStore } from '../../../modules/shared/store/common/infinite-scroll.store';
import {
    SocialServiceOfferObject,
} from '@libs/types/src/services/object-type/service/social-service/social-service-offer.object';
import {
    socialServiceOperationColor,
    SocialServiceOperationEnum,
} from '@libs/types/src/services/enum/social-service-operation.enum';
import {
    FolderIndividualPlanOfferUpsertInput,
} from '@libs/types/src/services/input-type/folder/folder-individual-plan-offer.upsert.input';
import { DateInputComponent } from '../../kit/input/date-input/date-input.component';
import { NumberInputComponent } from '../../kit/input/text-input/number-input.component';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import {
    SocialServiceSelectOfferComponent,
} from '../work/social-service-select-offer/social-service-select-offer.component';
import { TableComponent } from '../../../modules/shared/components/kit/table/table.component';
import { TextInputComponent } from '../../kit/input/text-input/text-input.component';
import { TranslatePipe } from '@ngx-translate/core';
import { dateify, dateifyOrNow } from '../../../modules/shared/helpers/date.helper';
import {
    FolderIndividualPlanOfferObject,
} from '@libs/types/src/services/object-type/folder/folder-individual-plan-offer.object';
import { offersScrollFrag, waitingListOffersFrag } from './social-service-offers-select-input.query';
import { FolderGlobalStore } from '../../../store/medicalService/folder.global-store';
import { SwitchInputComponent } from '../../kit/input/switch-input/switch-input.component';
import { TableColumn } from '../../../modules/shared/components/kit/table/table.types';
import { BaseDialogService } from '../../kit/base-dialog/base-dialog-service';
import { v4 as createUuid } from 'uuid';
import { StripAndShowComponent } from '../../../modules/shared/components/kit/strip-and-show/strip-and-show.component';
import { Chip } from '@openng/optimus-ui/chip';
import { ColorContrastPipe } from '../../../modules/shared/pipes/color-contrast.pipe';
import {
    DEFAULT_TAG_COLOR,
} from '../setting/service-settings-offers-page/service-settings-offer-detail-page/service-settings-offer-detail-page.component';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { NgStyle } from '@angular/common';
import { WaitingListObject } from '@libs/types/src/services/object-type/waiting-list.object';
import {
    WaitingListOfferExistsDialogComponent,
} from './waiting-list-offer-exists-dialog/waiting-list-offer-exists-dialog.component';

@Component({
    selector: 'app-social-service-offers-select-input',
    templateUrl: './social-service-offers-select-input.component.html',
    styleUrl: './social-service-offers-select-input.component.scss',
    providers: [SocialServiceOffersSelectInputStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DateInputComponent,
        NumberInputComponent,
        PrimeTemplate,
        SocialServiceSelectOfferComponent,
        TableComponent,
        TextInputComponent,
        TranslatePipe,
        SwitchInputComponent,
        StripAndShowComponent,
        CheckInputComponent,
        Chip,
        ColorContrastPipe,
        Tooltip,
        NgStyle,
    ],
})
export class SocialServiceOffersSelectInputComponent {

    public readonly store = inject(SocialServiceOffersSelectInputStore);
    public readonly folderStore = inject(FolderGlobalStore);
    public readonly dialogService = inject(BaseDialogService);

    public date = input<Date, any>(new Date(), { transform: v => new Date(v || new Date()) });
    public addValidFrom = input<Date | string | undefined | null>(null);
    public allowedTypes = input<string[]>([
        SocialServiceOperationEnum.BILL,
        SocialServiceOperationEnum.FACULTATIVE,
    ]);

    public folderId = input<number | null>(null);

    public disabled = input<boolean>(false);

    protected form = fb.group({
        showAllOffers: fb.control<boolean>(false),
        offers: fb.control<WorkOfferUpsertInput[]>([]),
    });

    protected showAllOffersChange = changeSignal(this.form.controls.showAllOffers);

    protected folderWaitingLists = computed(() => {
        return this.store.data.getWaitingListItems()?.['items'] || [];
    });

    protected offers: (FolderIndividualPlanOfferObject | any)[] = [];
    public offersData = signal<Record<number, any>>({});

    protected offerForms = signal<FormGroup[]>([]);
    protected visibleOfferForms = computed(() => {
        const all = this.offerForms();
        if (this.showAllOffersChange()) {
            return all;
        }

        return all
            .filter((it) => {
                const now = this.date();
                const from = it.value.dateFrom;
                const to = it.value.dateTo;

                return from <= now && (!to || to >= now);
            })
            ;
    });

    protected offerColumns = computed<TableColumn[]>(() => {
        return [
            { name: 'dateFrom' },
            this.showAllOffersChange() ? { name: 'dateTo' } : null,
            { name: 'code' },
            { name: 'name' },
            { name: 'unit' },
            { name: 'noRecord' },
            { name: 'usualTime' },
            { name: 'frequency' },
        ].filter((it) => it) as TableColumn[];
    });

    protected readonly operationChecks = viewChildren<CheckInputComponent>('operationCheck');
    protected readonly procedureChecks = viewChildren<CheckInputComponent>('procedureCheck');

    protected readonly additionalOffersFilter = computed(() => {
        const filter = new FilterBuilder();
        const offers = this.offerForms();
        const now = new Date();
        const validOffers = offers
            ?.map((it) => it.value)
            ?.filter((it: any) => it.dateFrom <= now && (!it.dateTo || it.dateTo >= now))
            ?.map((it: any) => it.offerId)
        ;
        if (validOffers?.length <= 0) return null;
        filter.where('id', validOffers, 'NIN');

        return filter.filter().filter;
    });

    public offersScroll = computed(() => {
        const filter = new FilterBuilder();
        filter.limit(100);
        filter.serviceId(this.folderStore.folder()?.serviceId || 0);
        filter.where('type', SocialServiceOperationEnum.BILL);
        const offers = this.offerForms();
        const now = new Date();
        filter.where('id', offers
            ?.map((it) => it.value)
            ?.filter((it: any) => it.dateFrom <= now && (!it.dateTo || it.dateTo >= now))
            ?.map((it: any) => it.offerId), 'NIN')
        ;

        return new InfiniteScrollStore<SocialServiceOfferObject>(
            this.store.api.getSocialServiceOffers,
            filter.filter(),
            true,
            offersScrollFrag.replaceAll('VALID_AT', now.toISOString()),
            (items) => items.map((it: SocialServiceOfferObject | any) => {
                const op = it.operations?.[0];
                const tag = op?.type || SocialServiceOperationEnum.BILL;

                return {
                    ...it,
                    tag,
                    tagColor: socialServiceOperationColor[tag],
                };
            }),
            true,
            'id',
            false,
        );
    });

    constructor() {
        this.store.onSuccess(() => this.fetchFolderWaitingLists(),
            'createIndividualPlan',
            'updateIndividualPlan');

        effect(() => {
            [...this.operationChecks(), ...this.procedureChecks()].forEach((op) => {
                const checked = this.offers
                    ?.find((offer) => offer.offerId == op.attributes().offerId)
                    ?.[op.attributes().type]
                    ?.includes(op.attributes().id)
                ;
                op.setSelected(checked);
            });
        });

        effect(() => {
            this.fetchFolderWaitingLists();
        });
    }

    public getOffers(): FolderIndividualPlanOfferUpsertInput[] {
        const operations = this.operationChecks() || [];
        const procedures = this.procedureChecks() || [];

        return this.offerForms().map((it) => {
            return <FolderIndividualPlanOfferUpsertInput>{
                id: !isNaN(it.value.id) ? it.value.id : null,
                offerId: it.value.offerId,
                usualTime: it.value.usualTime,
                restTime: it.value.restTime,
                noRecord: it.value.noRecord,
                frequency: it.value.frequency,
                dateFrom: it.value.dateFrom.toISOString(),
                dateTo: it.value.dateTo?.toISOString(),
                waitingListClosureDate: it.value.waitingListClosureDate?.toISOString(),
                operations: operations
                    .filter((op) => op.attributes().offerId == it.value.offerId)
                    .filter((op) => op.selected())
                    .map((it) => Number(it.id()))
                ,
                procedures: procedures
                    .filter((op) => op.attributes().offerId == it.value.offerId)
                    .filter((op) => op.selected())
                    .map((it) => Number(it.id()))
                ,
            };
        });
    }

    public async patchOffers(offers: any[]): Promise<void> {
        const offerForms: FormGroup[] = [];
        this.offers = [...offers];

        this.offersData.set(Object.fromEntries(
            offers.map(offer => [
                offer.offerId,
                {
                    ...offer.offer,
                    usualTime: offer.usualTime,
                    restTime: offer.waitingListItems?.reduce((acc: number, obj: WaitingListObject) => {
                        return acc + (obj.applications || 0);
                    }, 0),
                },
            ]),
        ) as Record<number, any>);

        this.offers.forEach((offer: any) => {
            const group = fb.group({
                id: fb.control<number | string>(offer?.id || createUuid()),
                usualTime: fb.control<number | null>(offer.usualTime),
                restTime: fb.control<number | null>(this.offersData()?.[offer.offerId].restTime),
                noRecord: fb.control<boolean>(offer.noRecord ?? offer.offer?.noRecord ?? false),
                offerId: fb.control(offer.offerId, Validators.required),
                frequency: fb.control<string | null>(offer.frequency),
                dateFrom: fb.control<Date>(dateifyOrNow(offer.dateFrom), Validators.required),
                dateTo: fb.control<Date | null>(dateify(offer.dateTo)),
                waitingListClosureDate: fb.control<Date | null>(null),
            });
            (group as any).__hideDelete = offer.hasWork || this.disabled();
            if (this.disabled()) {
                group.disable();
            }
            offerForms.push(group);
        });
        this.offerForms.set(offerForms);
    }

    protected async addOffer(offer: any): Promise<void> {
        if (!offer) return;

        const dateFrom = this.addValidFrom() || offer?.validFrom;

        const offers = this.offerForms().map((it) => it.value);

        const existingOffer = offers.find((it: any) => it.offerId == offer.id);
        if (existingOffer && (!existingOffer.dateTo || existingOffer.dateTo > new Date())) return;

        const existingWLOffer = this.folderWaitingLists().find((x: WaitingListObject) => x.offerId === offer.id);
        if (existingWLOffer) {
            this.dialogService.open(WaitingListOfferExistsDialogComponent, {
                header: translate.instant('waitingList.offerExists.title'),
                styleClass: 'p-dialog-width-40',
                data: { item: existingWLOffer },
            }).onClose.subscribe((res) => {
                if (res) {
                    this.patchOfferForms(offer, !existingOffer ? dateFrom : null, res.dateTo, res.applications, existingWLOffer.applications - res.applications);
                }
            });
        } else {
            this.patchOfferForms(offer, !existingOffer ? dateFrom : null, null, offer.usualApplications);
        }
    }

    protected removeOffer(offerForm: any): void {
        this.offerForms.update((it) => it.filter((it) => it.value.id !== offerForm.value.id));
        this.offersData.update((it: Record<number, any>) => {
            const updated = { ...it };
            delete updated[offerForm.value.offerId];

            return updated;
        });
    }

    /**
     * Helper due to fkin reactive programming late component init
     */
    protected selectedOp(offerId: number, id: number, type: 'procedures' | 'operations'): void {
        const offerIndex: any = this.offers?.findIndex((it) => it.offerId === offerId);
        let offer: any;

        if (offerIndex >= 0) {
            offer = { ...this.offers[offerIndex] };
            this.offers[offerIndex] = offer; // Angular's fcking read-only bullshit
        } else {
            offer = { offerId: offerId };
            this.offers.push(offer);
        }

        let items: any[] = offer[type] || [];
        if (!items.includes(id)) {
            offer[type] = [...items, id];
        }
    }

    protected usualTimeChange(item: any, usualTime: any) {
        if (!this.folderWaitingLists().some((it: any) => it.offerId === item?.value?.offerId)) return;

        const prevOffer = this.offersData()?.[item?.value?.offerId || ''];
        if (prevOffer) {
            this.offersData.update((it) => ({
                ...it,
                [prevOffer.id]: {
                    ...prevOffer,
                    usualTime,
                    restTime: prevOffer.usualTime + prevOffer.restTime - usualTime,
                },
            }));
            item.patchValue({
                usualTime,
                restTime: prevOffer.usualTime + prevOffer.restTime - usualTime,
            }, { emitEvent: false });
        }
    }

    private patchOfferForms(offer: any, dateFrom: Date | null = null, waitingListClosureDate: Date | null = null, applications: number | null = null, rest: number | null = null) {
        const group = fb.group({
            id: fb.control<number | string>(createUuid()),
            usualTime: fb.control<number | null>(applications || offer.usualTime),
            restTime: fb.control<number | null>(rest),
            noRecord: fb.control<boolean>(offer.noRecord ?? false),
            offerId: fb.control(offer.id, Validators.required),
            frequency: fb.control<string | null>(''),
            dateFrom: fb.control<Date>(dateifyOrNow(dateFrom), Validators.required),
            dateTo: fb.control<Date | null>(dateify(offer.validTo)),
            waitingListClosureDate: fb.control<Date | null>(dateify(waitingListClosureDate)),
        });

        this.offerForms.update((it) => [...it, group]);

        this.offersData.update((it) => ({
            ...it,
            [offer.id]: {
                ...offer,
                usualTime: applications || offer.usualTime,
                restTime: rest || 0,
            },
        }));
    }

    private async fetchFolderWaitingLists() {
        if (!this.folderId()) return;

        const filter = new FilterBuilder({
            folderId: this.folderId(),
        });
        filter.validByNow('dateFrom', 'dateTo');

        await this.store.api.getWaitingListItems(filter.filter(), waitingListOffersFrag);
    }

    protected readonly SocialServiceOperationEnum = SocialServiceOperationEnum;
    protected readonly DEFAULT_TAG_COLOR = DEFAULT_TAG_COLOR;
}
